import type { DeepLinkParts, DeepLinkSurface } from './deep-links';

/**
 * Markdown extension parser (agent-ux-optimization Phase 2 Session 5).
 *
 * Single regex pass that recognizes BOTH:
 *   - `[arcanum:input:<named_range>=<value>]` → `<ProvenanceChip>`
 *   - `[arcanum:goto:<surface>?<params>][#fragment]` → `<DeepLink>`
 *
 * Used by `<MessageBubble>`'s `renderMarkdown` line-walk: each line is first
 * split via `splitTextWithExtensions`; for resulting STRING segments the
 * existing bold/heading rules apply; for `MarkdownExtension` segments the
 * dispatcher materializes the right component placeholder.
 *
 * Target group `[^=?\]#]+` MUST exclude `?` and `#` so they terminate target
 * capture and feed the dedicated query / hash groups (B1 fix from the
 * /gap-analysis third pass — the earlier draft's `[^=\]]+` greedy-ate
 * `model?cell=...` for the goto form).
 */
export const ARCANUM_EXTENSION_REGEX =
  /\[arcanum:(input|goto):([^=?\]#]+)(?:=([^\]]+))?(?:\?([^\]#]+))?(?:#([^\]]+))?\]/g;

export type MarkdownExtension =
  | { kind: 'provenance'; namedRange: string; value: string; raw: string }
  | { kind: 'deep-link'; parts: DeepLinkParts };

const KNOWN_SURFACES = new Set<DeepLinkSurface>([
  'workbench', 'signal', 'model', 'variables',
]);

/**
 * Split a text chunk into interleaved string segments and `MarkdownExtension`
 * matches. The output preserves order; concatenating string segments and
 * reserialized extensions reproduces the input exactly.
 *
 * Empty input → empty array. Input with no extensions → single string element.
 */
export function splitTextWithExtensions(text: string): Array<string | MarkdownExtension> {
  if (text === '') return [];
  const out: Array<string | MarkdownExtension> = [];
  let lastIdx = 0;

  // matchAll preserves a per-call lastIndex internally; the global flag on
  // ARCANUM_EXTENSION_REGEX is required by matchAll's contract.
  for (const match of text.matchAll(ARCANUM_EXTENSION_REGEX)) {
    const start = match.index ?? 0;
    if (start > lastIdx) out.push(text.slice(lastIdx, start));

    const [raw, kind, target, value, query, hash] = match;
    if (kind === 'input') {
      out.push({
        kind: 'provenance',
        namedRange: target,
        // Value is intentionally NOT URL-decoded here — the consumer
        // (`<ProvenanceChip>`) decodes for display when needed; raw form
        // round-trips cleanly through `serializeDeepLink`-style handling.
        value: value ?? '',
        raw,
      });
    } else {
      // kind === 'goto'
      const params: Record<string, string> = {};
      if (query) {
        for (const pair of query.split('&')) {
          const [k, v] = pair.split('=');
          if (!k) continue;
          params[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
        }
      }
      // Unknown surfaces fall through as literal text — the agent's grammar
      // is closed and emit-side; defensively rendering the source as text
      // beats silently dropping it.
      const surface = target as DeepLinkSurface;
      if (!KNOWN_SURFACES.has(surface)) {
        out.push(raw);
      } else {
        out.push({
          kind: 'deep-link',
          parts: {
            surface,
            params,
            hash: hash ? decodeURIComponent(hash) : null,
            raw,
          },
        });
      }
    }
    lastIdx = start + raw.length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}
